export type TrackCatalogTrackFeaturePoint = {
  uuid: string;
  type: string;
  label: string;
  description?: string | null;
  pointAsText: string;
  trackUuid: string;
};

export type TrackCatalogTrack = {
  uuid: string;
  country: string;
  name: string;
  locationAsText: string;
  isBeta?: boolean | null;
  isFree?: boolean | null;
  featurePoints: TrackCatalogTrackFeaturePoint[];
};

export type TrackCatalogSegmentLine = {
  uuid: string;
  parentUuid?: string | null;
  segmentType: string;
  label: string;
  entryLineAsText?: string | null;
  exitLineAsText?: string | null;
  entryDirectionalPointAsText?: string | null;
  polygonAsText?: string | null;
  trackConfigUuid: string;
};

export type TrackCatalogTrackConfig = {
  uuid: string;
  name: string;
  length?: number | null;
  centreLineAsText?: string | null;
  polygonAsText: string;
  trackUuid: string;
  segmentLines: TrackCatalogSegmentLine[];
};

export type TrackCatalogV2Segment = {
  trackConfigSegmentUuid: string;
  segmentType: string;
  polygonAsText: string;
  boundingBoxAsText: string;
};

export type TrackCatalogV2TrackConfig = {
  trackConfigUuid: string;
  boundingBoxAsText: string;
  country: string;
  segments: TrackCatalogV2Segment[];
};

export type TrackCatalogMapLayers = {
  trackOutline: boolean;
  corners: boolean;
  straights: boolean;
  sectors: boolean;
  lines: boolean;
  labels: boolean;
};

export type WktCoordinate = {
  lat: number;
  lng: number;
};

const NUMBER_PATTERN = '-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?';
const COORDINATE_PATTERN = new RegExp(`(${NUMBER_PATTERN})\\s+(${NUMBER_PATTERN})`, 'g');

function parseCoordinateGroup(group: string): WktCoordinate[] {
  const coordinates: WktCoordinate[] = [];
  for (const match of group.matchAll(COORDINATE_PATTERN)) {
    const lng = Number(match[1]);
    const lat = Number(match[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      coordinates.push({ lat, lng });
    }
  }
  return coordinates;
}

/**
 * Parse the POINT, LINESTRING, and POLYGON shapes used by the local
 * TrackCatalog export. Polygon rings stay separate so holes render correctly.
 */
export function parseTrackCatalogWkt(wkt?: string | null): WktCoordinate[][] {
  if (!wkt?.trim()) {
    return [];
  }

  const groups = [...wkt.matchAll(/\(([^()]+)\)/g)]
    .map((match) => parseCoordinateGroup(match[1] ?? ''))
    .filter((coordinates) => coordinates.length > 0);

  if (groups.length > 0) {
    return groups;
  }

  const coordinates = parseCoordinateGroup(wkt);
  return coordinates.length > 0 ? [coordinates] : [];
}

export function trackCatalogSegmentColor(segmentType: string) {
  switch (segmentType.toLowerCase()) {
    case 'corner':
      return '#ff5f6d';
    case 'straight':
      return '#28c8ff';
    case 'sector':
      return '#b57cff';
    case 'startfinish':
    case 'start/finish':
      return '#ffe66d';
    case 'kerbmarker':
      return '#f97316';
    case 'gridmarker':
      return '#22c55e';
    case 'distancemarker':
      return '#38bdf8';
    case 'trackoutsideboundary':
      return '#f8fafc';
    case 'trackinsideboundary':
      return '#cbd5e1';
    case 'tracksurface':
      return '#28c8ff';
    default:
      return '#f4f7fb';
  }
}

export function trackCatalogSegmentVisible(segmentType: string, layers: TrackCatalogMapLayers) {
  switch (segmentType.toLowerCase()) {
    case 'corner':
      return layers.corners;
    case 'straight':
      return layers.straights;
    case 'sector':
      return layers.sectors;
    default:
      return true;
  }
}

export function wktPathCenter(paths: WktCoordinate[][]): WktCoordinate | null {
  const points = paths.flat();
  if (points.length === 0) {
    return null;
  }

  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}
